import { routePaths } from 'appConfig';
import { Button } from 'components/common';
import {
  ProductCategory,
  SpecialProductCategory,
  useCategories,
} from 'lib/productCategory';
import { Tag, useTags } from 'lib/tag';
import { useEffect, useState } from 'react';
import QuickMenuView from './QuickMenuView';
import { QuickMenuSection } from './types';
import { Collection, useCollections } from 'lib/collections';
import useMediaQuery from 'hooks/useMediaQuery';
import QuickMenuModal from './QuickMenuModal';
import { Catalogue, useCatalogues } from 'lib/catalogues';

interface SeeMenuProps {
  catalogueIds?: string;
  categories: ProductCategory[];
  collections: Collection[];
  specialProductCategories: SpecialProductCategory[];
}

const SeeMenu = ({
  catalogueIds,
  categories = [],
  collections = [],
  specialProductCategories = [],
}: SeeMenuProps) => {
  const [showMenuModal, setShowMenuModal] = useState(false);

  const { tags, isLoading: isFetchingTags } = useTags({
    page: null,
    sizePerPage: null,
  });

  const {
    catalogues,
    isLoading: isFetchingCatalogues,
  } = useCatalogues();

  const getKcalRange = (title: string) => {
    const match = title.match(/\d+/g); // Extract numbers from title
    if (match && match.length === 2) {
      return parseInt(match[0]); // Extract the lower bound of kcal range
    }
    return 0;
  };

  // const generateNewCatalogueTitle = (lowerBound: number) => {
  //   if (lowerBound <= 400) return 'Meals with 400 Kcal or less';
  //   if (lowerBound <= 450) return 'Meals between 401-450 Kcal';
  //   if (lowerBound <= 500) return 'Meals between 451-500 Kcal';
  //   if (lowerBound <= 550) return 'Meals between 501-550 Kcal';
  //   return 'Unknown';
  // };

  const selectedCatalogueIds = catalogueIds?.split(',') ?? [];
  const sortedCatalogues = catalogues
    .filter((catalogue) =>
      selectedCatalogueIds.includes(catalogue.slug),
    )
    .sort((a, b) => getKcalRange(a.title) - getKcalRange(b.title))
    .map((catalogue) => catalogue);

  const quickMenuSections: QuickMenuSection[] = [
    {
      title: 'Menu',
      menus: [
        {
          items: categories.map((category: ProductCategory) => ({
            label: category.name,
            url: `${routePaths.PRODUCTS}?${category.id
              .split(',')
              .map((id) => `category_ids=${id}`)
              .join('&')}`,
          })),
        },
        {
          items: collections.map((collection: Collection) => ({
            label: collection.name,
            url: `${routePaths.PRODUCTS}?collection_ids=${collection.id}`,
          })),
        },
        {
          items: specialProductCategories.map(
            (category: SpecialProductCategory) => ({
              label: category.name,
              url: `${routePaths.PRODUCTS}?${category.id
                .split(',')
                .map((id) => `category_ids=${id}`)
                .join('&')}`,
            }),
          ),
        },
        {
          isLoading: isFetchingCatalogues,
          items: sortedCatalogues.map((catalogue: Catalogue) => ({
            label: catalogue.title,
            url: `${routePaths.CATALOGUES}/${catalogue.slug}`,
          })),
        },
      ],
    },
    {
      title: 'Food Groups & Ingredients',
      menus: [
        {
          isLoading: isFetchingTags,
          items: tags.map((tag: Tag) => ({
            label: tag.name,
            url: `${routePaths.PRODUCTS}?tag_ids=${tag.id}`,
          })),
        },
      ],
    },
  ];

  const isMobileViewport = useMediaQuery('(max-width: 480px)');
  const isPointerCoarse = useMediaQuery('(pointer: coarse)');

  const isMobile = isMobileViewport && isPointerCoarse;

  useEffect(() => {
    if (!isMobile) setShowMenuModal(false);
  }, [isMobile]);

  if (isMobile) {
    return (
      <>
        <Button
          className="btn-outline-dark-3 btn-more tw-py-5 tw-px-6 tw-border tw-leading-none tw-text-[#777] tw-border-[#dadada]"
          onClick={() => setShowMenuModal(true)}
        >
          <>
            <span>SEE MENU</span>
            <i className="icon-long-arrow-right"></i>
          </>
        </Button>
        <QuickMenuModal
          isOpen={showMenuModal}
          onHide={() => setShowMenuModal(false)}
          menus={quickMenuSections}
        />
      </>
    );
  }

  return (
    <div className="dropdown menu-dropdown">
      <div
        className="dropdown-toggle btn btn-outline-dark-3 btn-more tw-py-5 tw-px-6 tw-border tw-border-[#dadada] tw-text-[#777] hover:tw-text-white tw-leading-none tw-relative after:tw-border-none"
        role="button"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        data-display="static"
      >
        <span>SEE MENU</span>
        <i className="icon-long-arrow-right"></i>
      </div>
      <div className="dropdown-menu tw-bg-white tw-rounded-md tw-p-8 tw-shadow-xl tw-border-none tw-w-[320px] md:tw-w-[460px] lg:tw-w-[560px] tw-h-[420px] -tw-left-full md:-tw-translate-x-1/3 lg:tw-left-1/2 lg:tw-right-auto lg:-tw-translate-x-1/2 tw-overflow-y-auto">
        <div className="tw-grid tw-grid-col-1 md:tw-grid-cols-2 tw-gap-8">
          {quickMenuSections.map(({ title, menus }) => (
            <QuickMenuView key={title} title={title} menus={menus} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeeMenu;
